/**
 * ManagerReviewQueue — PUSH defenses to review + timing override requests.
 *
 * Renders two queue widgets for the manager dashboard:
 *   1. PUSH defense adequacy decisions awaiting the manager (S-CP10-3-A
 *      `manager_judges` mode — Partner default; rep_self_check tenants see
 *      empty queue).
 *   2. Q5.9 `window_blackout` override requests pending approve/deny
 *      (S-CP5-1 — full RULE-ESC-1 audit path with free-text reason).
 *
 * Data source is the v_manager_review_queue Supabase view when present; falls
 * back to demo-tenant seed otherwise. The widget reads tenant adequacy mode
 * from getCurrentTenant().config to decide whether to render the PUSH section.
 */
import { useEffect, useState } from "react";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import { tokens } from "@/components/operator/tokens";

type QueueItem = {
  id: string;
  kind: "push_adequacy" | "q59_override";
  account_name: string;
  rep_name: string;
  reason: string;
  age_hours: number;
  deal_id?: number;
};

interface Props {
  /** When set, only this tenant's queue is shown (manager scope). */
  tenantId?: number;
}

const DEMO_PUSH: QueueItem[] = [
  {
    id: "p1",
    kind: "push_adequacy",
    account_name: "Northgate Aerospace",
    rep_name: "M. Chen",
    reason: "Customer confirmed Q3 budget release; awaiting procurement contact handoff",
    age_hours: 14,
    deal_id: 4,
  },
  {
    id: "p2",
    kind: "push_adequacy",
    account_name: "Vertex Energy",
    rep_name: "A. Patel",
    reason: "FY26 renewal pulled forward — board mandate signed last Tuesday",
    age_hours: 41,
    deal_id: 2,
  },
];

const DEMO_Q59: QueueItem[] = [
  {
    id: "q1",
    kind: "q59_override",
    account_name: "Cascade Mfg",
    rep_name: "B. Short",
    reason: "Holiday freeze window — customer has internal exception, signing 12/27",
    age_hours: 6,
    deal_id: 3,
  },
];

function Section({
  title, items, emptyMsg, onRowClick,
}: {
  title: string;
  items: QueueItem[];
  emptyMsg: string;
  onRowClick?: (item: QueueItem) => void;
}) {
  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: 12,
        background: "#FFFFFF",
        overflow: "hidden",
        marginBottom: 12,
      }}
      data-testid={`mrq-${title.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${tokens.color.border}`,
          background: "#FAFAF7",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: tokens.color.textPrimary, letterSpacing: "0.04em" }}>
          {title}
        </span>
        <span
          style={{
            background: items.length > 0 ? "#FEF3C7" : "#F4F4F5",
            color: items.length > 0 ? "#92400E" : tokens.color.textMuted,
            border: items.length > 0 ? "1px solid #FDE68A" : "1px solid #E5E5E7",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
          }}
        >
          {items.length} pending
        </span>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: "14px", color: tokens.color.textMuted, fontSize: 12 }}>{emptyMsg}</div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.map((it, idx) => (
            <li
              key={it.id}
              onClick={onRowClick ? () => onRowClick(it) : undefined}
              style={{
                padding: "10px 14px",
                borderTop: idx === 0 ? "none" : `1px solid ${tokens.color.borderSoft}`,
                cursor: onRowClick ? "pointer" : "default",
                fontSize: 13,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontWeight: 600, color: tokens.color.textPrimary }}>{it.account_name}</div>
                <div style={{ fontSize: 11, color: tokens.color.textMuted, whiteSpace: "nowrap" }}>
                  {it.rep_name} · {it.age_hours}h ago
                </div>
              </div>
              <div style={{ fontSize: 12, color: tokens.color.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                {it.reason}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ManagerReviewQueue({ tenantId: tenantIdProp }: Props) {
  const session = getSession();
  const tenant = getCurrentTenant();
  const tenantId = tenantIdProp ?? session?.tenantId ?? 2;
  const isDemo = tenant?.is_demo === true;

  // Adequacy check mode controls whether PUSH section renders. Default to
  // rep_self_check; Partner is manager_judges (S-CP10-3-A).
  const adequacyMode: "rep_self_check" | "manager_judges" =
    (tenant as any)?.config?.adequacy_mode
    ?? (tenantId === 1 ? "manager_judges" : "rep_self_check");

  const [push, setPush] = useState<QueueItem[]>([]);
  const [q59, setQ59] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const { data, error } = await sb
          .from("v_manager_review_queue")
          .select("id, kind, account_name, rep_name, reason, age_hours, deal_id")
          .eq("tenant_id", tenantId);
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          if (isDemo) {
            setPush(adequacyMode === "manager_judges" ? DEMO_PUSH : []);
            setQ59(DEMO_Q59);
          } else {
            setPush([]); setQ59([]);
          }
        } else {
          setPush(data.filter((x: any) => x.kind === "push_adequacy"));
          setQ59(data.filter((x: any) => x.kind === "q59_override"));
        }
      } catch {
        if (isDemo) {
          setPush(adequacyMode === "manager_judges" ? DEMO_PUSH : []);
          setQ59(DEMO_Q59);
        } else {
          setPush([]); setQ59([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, isDemo, adequacyMode]);

  if (loading) {
    return (
      <div style={{ fontSize: 12, color: tokens.color.textMuted, padding: 12 }}>
        Loading review queue…
      </div>
    );
  }

  return (
    <div data-testid="manager-review-queue">
      {adequacyMode === "manager_judges" && (
        <Section
          title="PUSH defenses to review"
          items={push}
          emptyMsg="No PUSH defenses awaiting your judgment."
        />
      )}
      {adequacyMode === "rep_self_check" && (
        <div
          style={{
            background: "#F4F4F5",
            border: `1px dashed ${tokens.color.border}`,
            borderRadius: 12,
            padding: "10px 14px",
            fontSize: 12,
            color: tokens.color.textMuted,
            marginBottom: 12,
          }}
        >
          Tenant adequacy mode: <strong>rep_self_check</strong>. Reps confirm
          their own PUSH defense. You will see attention flags only when a
          rep marks Q10.PUSH.4 = NO.
        </div>
      )}
      <Section
        title="Timing override requests"
        items={q59}
        emptyMsg="No blackout window override requests pending."
      />
    </div>
  );
}
